/**
 * Storage Adapter Interface
 * 
 * Abstraction layer for data persistence to enable testability.
 * Allows injecting different storage implementations (IndexedDB for production, in-memory for tests).
 */

export interface StorageAdapter {
  /**
   * Add a record to a store
   */
  add<T>(storeName: string, value: T): Promise<void>;

  /**
   * Get all records from a store, optionally filtered
   */
  getAll<T>(storeName: string, query?: string): Promise<T[]>;

  /**
   * Clear all records from a store
   */
  clear(storeName: string): Promise<void>;

  /**
   * Count records in a store
   */
  count(storeName: string): Promise<number>;
}

/**
 * In-Memory Storage Adapter
 * 
 * Simple in-memory implementation for testing purposes.
 * Does not persist data between sessions.
 */
export class InMemoryStorageAdapter implements StorageAdapter {
  private stores: Map<string, any[]> = new Map();

  async add<T>(storeName: string, value: T): Promise<void> {
    if (!this.stores.has(storeName)) {
      this.stores.set(storeName, []);
    }
    this.stores.get(storeName)!.push(value);
  }

  async getAll<T>(storeName: string, query?: string): Promise<T[]> {
    return (this.stores.get(storeName) || []) as T[];
  }

  async clear(storeName: string): Promise<void> {
    this.stores.delete(storeName);
  }

  async count(storeName: string): Promise<number> {
    return (this.stores.get(storeName) || []).length;
  }

  /**
   * Reset all stores (useful for test cleanup)
   */
  reset(): void {
    this.stores.clear();
  }
}

/**
 * IndexedDB Storage Adapter
 * 
 * Production implementation using IndexedDB with automatic fallback to InMemoryStorage.
 */
export class IndexedDBStorageAdapter implements StorageAdapter {
  private db?: IDBDatabase;
  private fallback?: InMemoryStorageAdapter;
  private initPromise?: Promise<void>;
  private isInitialized = false;

  constructor(
    providedDB?: IDBDatabase,
    private dbName = 'ybr-tracking-db',
    private version = 1,
    private stores: string[] = ['points', 'sessions']
  ) {
    // Force in-memory adapter in test environment
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
      console.log('[StorageAdapter] Test environment detected, using InMemoryStorageAdapter');
      this.fallback = new InMemoryStorageAdapter();
      return;
    }

    // If a DB instance was provided, use it directly
    if (providedDB) {
      this.db = providedDB;
      this.isInitialized = true;
    }
  }

  /**
   * Lazy init - called automatically on first usage
   */
  private async ensureInitialized(): Promise<void> {
    // If DB was provided in constructor, already initialized
    if (this.isInitialized) return;
    if (this.initPromise) {
      await this.initPromise;
      return;
    }

    this.initPromise = this.init();
    await this.initPromise;
    this.isInitialized = true;
  }

  private async init(): Promise<void> {
    // Check if IndexedDB exists (not available in Node/JSDOM)
    if (typeof indexedDB === 'undefined') {
      console.warn('IndexedDB not available, using in-memory storage');
      this.fallback = new InMemoryStorageAdapter();
      return;
    }

    try {
      await new Promise<void>((resolve, reject) => {
        const req = indexedDB.open(this.dbName, this.version);

        req.onupgradeneeded = (event) => {
          const db = req.result;
          
          // Create 'points' store with timestamp key and sessionId index
          if (!db.objectStoreNames.contains('points')) {
            const pointsStore = db.createObjectStore('points', { keyPath: 'timestamp' });
            pointsStore.createIndex('by-session', 'sessionId');
          }
          
          // Create 'sessions' store with id key
          if (!db.objectStoreNames.contains('sessions')) {
            db.createObjectStore('sessions', { keyPath: 'id' });
          }
        };

        req.onsuccess = () => {
          this.db = req.result;
          resolve();
        };

        req.onerror = () => reject(req.error);
      });
    } catch (error) {
      console.warn('IndexedDB initialization failed, using in-memory storage', error);
      this.fallback = new InMemoryStorageAdapter();
    }
  }

  async add<T>(storeName: string, value: T): Promise<void> {
    await this.ensureInitialized();

    if (this.fallback) {
      return this.fallback.add(storeName, value);
    }

    // Double protection: verify db exists after initialization
    if (!this.db) {
      console.warn('DB not initialized after ensureInit, using fallback');
      this.fallback = new InMemoryStorageAdapter();
      return this.fallback.add(storeName, value);
    }

    await new Promise<void>((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readwrite');
      // Use put() instead of add() to allow upsert and prevent ConstraintError
      tx.objectStore(storeName).put(value);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getAll<T>(storeName: string, query?: string): Promise<T[]> {
    await this.ensureInitialized();

    if (this.fallback) {
      return this.fallback.getAll<T>(storeName);
    }

    if (!this.db) return [];

    return new Promise<T[]>((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).getAll();
      req.onsuccess = () => resolve(req.result as T[]);
      req.onerror = () => reject(req.error);
    });
  }

  async clear(storeName: string): Promise<void> {
    await this.ensureInitialized();

    if (this.fallback) {
      return this.fallback.clear(storeName);
    }

    if (!this.db) return;

    await new Promise<void>((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async count(storeName: string): Promise<number> {
    await this.ensureInitialized();

    if (this.fallback) {
      return this.fallback.count(storeName);
    }

    if (!this.db) return 0;

    return new Promise<number>((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
}
